import React, { useEffect, useState } from "react";
import { Loader2, UserRound } from "lucide-react";
import { docsErr } from "@/lib/docsApi";

/** "My Profile" step shown before documents. Staff & partners fill it in; residents confirm what's on file from intake. */
export function ProfileStep({ api, path, onDone, prefix = "profile" }) {
  const [p, setP] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true; // ignore late responses (e.g. StrictMode double effect) so they can't wipe what the person already typed
    api.get(path).then((d) => { if (live) { setP(d); setVals(d.values); } }).catch((e) => live && setErr(docsErr(e)));
    return () => { live = false; };
  }, [api, path]);
  if (!p) return err ? <p className="text-sm text-red-300" data-testid={`${prefix}-error`}>{err}</p> : <div className="py-6 flex justify-center text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const save = async () => { setBusy(true); setErr(""); try { await api.put(path, { values: vals }); onDone(); } catch (e) { setErr(docsErr(e)); } finally { setBusy(false); } };
  return (
    <div data-testid={prefix} className="space-y-4 rounded-md border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2 text-white"><UserRound className="h-5 w-5 text-[#d4a017]" /><h3 className="text-lg font-medium">Step 1 · Your profile</h3></div>
      <p className="text-sm text-white/70">{p.editable ? "Fill this in once. Your details are filled into every document for you." : "This is what we have from your intake. It fills in your documents automatically. Check it's right."}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {p.fields.map((f) => p.editable ? (
          <label key={f.key} className="block space-y-1"><span className="text-xs text-white/60">{f.label}{f.required ? " *" : ""}</span>
            <input value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`${prefix}-field-${f.key}`}
              className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-white outline-none focus:border-[#d4a017]" /></label>
        ) : (
          <div key={f.key} data-testid={`${prefix}-value-${f.key}`}><div className="text-xs text-white/50">{f.label}</div><div className="text-white/90">{p.values[f.key] || <span className="text-white/40">Not on file</span>}</div></div>
        ))}
      </div>
      {err && <p className="text-sm text-red-300" data-testid={`${prefix}-save-error`}>{err}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy} onClick={save} data-testid={`${prefix}-save`} className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium bg-[#d4a017] text-black disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{p.editable ? "Save and continue" : "Yes, this is me. Continue"}</button>
        {!p.editable && <span className="text-xs text-white/50">Something wrong? Tell your case manager and they'll fix it.</span>}
      </div>
    </div>
  );
}
